/**
 * React hook for reading and mutating the persisted entry intent.
 * Use this throughout the onboarding flows to branch behaviour by entry point.
 */

import { useCallback } from 'react';
import { useEntryIntentStore } from '../stores/entryIntentStore';
import type { EntryIntent } from '../types/entryIntent.types';

interface UseEntryIntentReturn {
  intent: EntryIntent | null;
  hasHydrated: boolean;
  setIntent: (intent: EntryIntent) => void;
  patchIntent: (patch: Partial<EntryIntent>) => void;
  clearIntent: () => void;
}

export function useEntryIntent(): UseEntryIntentReturn {
  const intent = useEntryIntentStore((s) => s.intent);
  const hasHydrated = useEntryIntentStore((s) => s._hasHydrated);
  const setIntent = useEntryIntentStore((s) => s.setIntent);
  const patchIntent = useEntryIntentStore((s) => s.patchIntent);
  const clearIntent = useEntryIntentStore((s) => s.clearIntent);

  const stableSet = useCallback((next: EntryIntent) => setIntent(next), [setIntent]);
  const stablePatch = useCallback((patch: Partial<EntryIntent>) => patchIntent(patch), [patchIntent]);
  const stableClear = useCallback(() => clearIntent(), [clearIntent]);

  return {
    intent,
    hasHydrated,
    setIntent: stableSet,
    patchIntent: stablePatch,
    clearIntent: stableClear,
  };
}
